/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { PERMISSIONS } from '@squiz/dxp-auth';
import {
  ICON_AIRLINE_STOPS,
  ICON_AUTO_FIX_HIGH,
  ICON_DASHBOARD,
  ICON_MISCELLANEOUS_SERVICES,
  ICON_PENDING_ACTIONS,
  NavigationItem,
} from '@squiz/sds';
import { AI_STRINGS } from 'apps/artificial-intelligence/src/Constants/Strings.constants';
import { TENANT_FEATURE_FLAGS } from 'apps/console-core/src/Constants/FeatureFlags.constants';
import { ICON_ALT_ROUTE, ICON_FOLDER_ZIP } from 'libs/sds/src/Icons';
import React from 'react';
import { AiContentTools } from '../Screens/AiContentTools/AiContentTools';
import { AiViewContentTool } from '../Screens/AiViewContentTool/AiViewContentTool';
import { ConfigurationDashboard } from '../Screens/ConfigurationDashboard/ConfigurationDashboard';
import { DamIntegrations } from '../Screens/DamIntegrations/DamIntegrations';
import { DamIntegrationDetails } from '../Screens/DamIntegrations/screens/DamIntegrationDetails/DamIntegrationDetails';
import { EventSubscriptions } from '../Screens/EventSubscriptions/EventSubscriptions';
import { EventSubscriptionBuilderCreate } from '../Screens/EventSubscriptions/elements/EventSubscriptionBuilderCreate/EventSubscriptionBuilderCreate';
import { EventSubscriptionBuilderEdit } from '../Screens/EventSubscriptions/elements/EventSubscriptionBuilderEdit/EventSubscriptionBuilderEdit';
import { JobContexts } from '../Screens/JobContexts/JobContexts';
import { JobExecutions } from '../Screens/JobExecutions/JobExecutions';
import { Jobs } from '../Screens/Jobs/Jobs';
import { ServiceSettings } from '../Screens/ServiceSettings/ServiceSettings';
import { StaticSites } from '../Screens/StaticSites/StaticSites';
import { ManageRouting } from '../Screens/StaticSites/screens/ManageRouting/ManageRouting';
import { screenPaths } from './ScreenPaths.constants';
import { CONFIGURATION_STRINGS } from './Strings.constants';

/**
 * Configuration dashboard screen.
 */
export const CONFIGURATION_DASHBOARD: NavigationItem = {
  icon: ICON_DASHBOARD,
  key: `configuration-dashboard`,
  label: CONFIGURATION_STRINGS.dashboard,
  path: screenPaths.configurationDashboard,
  screen: <ConfigurationDashboard />,
};

/**
 * AI content tools screen.
 */
export const AI_CONTENT_TOOLS: NavigationItem = {
  featureFlag: TENANT_FEATURE_FLAGS.CONTENT_TOOLS_ACCESS,
  icon: ICON_AUTO_FIX_HIGH,
  key: `aiContentTools`,
  label: AI_STRINGS.aiContentTools,
  path: screenPaths.aiContentTools,
  permission: PERMISSIONS.dxpAi.GENERATIVE_READ,
  screen: <AiContentTools />,
};

/**
 * AI content tool screen.
 */
export const AI_VIEW_CONTENT_TOOL: NavigationItem = {
  devices: [],
  featureFlag: TENANT_FEATURE_FLAGS.CONTENT_TOOLS_ACCESS,
  key: `aiContentToolView`,
  label: AI_STRINGS.viewContentTool,
  path: screenPaths.aiContentToolView,
  permission: PERMISSIONS.dxpAi.GENERATIVE_READ,
  screen: <AiViewContentTool />,
};

/**
 * Service settings screen.
 */
export const SERVICE_SETTINGS: NavigationItem = {
  icon: ICON_MISCELLANEOUS_SERVICES,
  key: `service-settings`,
  label: CONFIGURATION_STRINGS.serviceSettings,
  path: screenPaths.serviceSettings,
  screen: <ServiceSettings />,
};

/**
 * Jobs screen.
 */
export const JOBS: NavigationItem = {
  featureFlag: TENANT_FEATURE_FLAGS.JOB_RUNNER_ACCESS,
  key: `jobs`,
  label: CONFIGURATION_STRINGS.jobs,
  path: screenPaths.jobs,
  permission: PERMISSIONS.dxpJobRunner.JOB_READ,
  screen: <Jobs />,
};

/**
 * Job contexts screen.
 */
export const JOB_CONTEXTS: NavigationItem = {
  featureFlag: TENANT_FEATURE_FLAGS.JOB_RUNNER_ACCESS,
  key: `job-contexts`,
  label: CONFIGURATION_STRINGS.contexts,
  path: screenPaths.jobContexts,
  permission: PERMISSIONS.dxpJobRunner.JOB_CONTEXT_READ,
  screen: <JobContexts />,
};

/**
 * Job runner screen group.
 */
export const JOB_RUNNER: NavigationItem = {
  children: [JOBS, JOB_CONTEXTS],
  featureFlag: TENANT_FEATURE_FLAGS.JOB_RUNNER_ACCESS,
  icon: ICON_PENDING_ACTIONS,
  key: `job-runner`,
  label: CONFIGURATION_STRINGS.jobRunner,
  path: screenPaths.jobRunner,
  permission: PERMISSIONS.dxpJobRunner.JOB_READ,
};

/**
 * Job execution screen.
 */
export const JOB_EXECUTION: NavigationItem = {
  devices: [],
  featureFlag: TENANT_FEATURE_FLAGS.JOB_RUNNER_ACCESS,
  key: `job-execution`,
  label: CONFIGURATION_STRINGS.jobExecution,
  path: screenPaths.jobExecutions,
  permission: PERMISSIONS.dxpJobRunner.JOB_EXECUTION_READ,
  screen: <JobExecutions />,
};

/**
 * Event subscriptions screen.
 */
export const EVENT_SUBSCRIPTIONS: NavigationItem = {
  key: `event-subscriptions`,
  label: CONFIGURATION_STRINGS.eventSubscriptions,
  path: screenPaths.eventSubscriptions,
  permission: PERMISSIONS.dxpEventBus.EVENT_BUS_SUBSCRIPTION_READ,
  screen: <EventSubscriptions />,
};

/**
 * Create event subscriptions screen.
 */
export const EVENT_SUBSCRIPTIONS_BUILDER_CREATE: NavigationItem = {
  devices: [],
  key: `event-subscriptions-builder-create`,
  label: CONFIGURATION_STRINGS.createEventSubscription,
  path: screenPaths.eventSubscriptionBuilderCreate,
  permission: PERMISSIONS.dxpEventBus.EVENT_BUS_SUBSCRIPTION_WRITE,
  screen: <EventSubscriptionBuilderCreate />,
};

/**
 * Edit event subscriptions screen.
 */
export const EVENT_SUBSCRIPTIONS_BUILDER_EDIT: NavigationItem = {
  devices: [],
  key: `event-subscriptions-builder-edit`,
  // Screen title will be the name of the event subscription
  label: ``,
  path: screenPaths.eventSubscriptionBuilderEdit,
  permission: PERMISSIONS.dxpEventBus.EVENT_BUS_SUBSCRIPTION_WRITE,
  screen: <EventSubscriptionBuilderEdit />,
};

/**
 * Event service screen group.
 */
export const EVENT_SERVICE: NavigationItem = {
  children: [EVENT_SUBSCRIPTIONS],
  icon: ICON_AIRLINE_STOPS,
  key: `event-service`,
  label: CONFIGURATION_STRINGS.eventService,
  path: screenPaths.eventService,
  permission: PERMISSIONS.dxpEventBus.EVENT_BUS_SUBSCRIPTION_READ,
};

/**
 * DAM integrations screen.
 */
export const DAM_INTEGRATIONS: NavigationItem = {
  featureFlag: TENANT_FEATURE_FLAGS.DAM_INTEGRATIONS_ACCESS,
  icon: ICON_FOLDER_ZIP,
  key: `dam-integrations`,
  label: CONFIGURATION_STRINGS.damIntegrations,
  path: screenPaths.damIntegrations,
  permission: PERMISSIONS.dxpContentStore.TENANT_SETTING_READ,
  screen: <DamIntegrations />,
};

/**
 * DAM integration details screen.
 */
export const DAM_INTEGRATION_DETAILS: NavigationItem = {
  devices: [],
  featureFlag: TENANT_FEATURE_FLAGS.DAM_INTEGRATIONS_ACCESS,
  key: `dam-integration-details`,
  label: CONFIGURATION_STRINGS.damIntegrations,
  path: screenPaths.damIntegrationDetails,
  permission: PERMISSIONS.dxpContentStore.TENANT_SETTING_READ,
  screen: <DamIntegrationDetails />,
};

/*
 * Static sites screen.
 */
export const STATIC_SITES: NavigationItem = {
  icon: ICON_ALT_ROUTE,
  key: 'static-sites',
  label: CONFIGURATION_STRINGS.staticSites,
  path: screenPaths.staticSites,
  screen: <StaticSites />,
};

/**
 * Static sites manage routing screen.
 */
export const STATIC_SITES_MANAGE_ROUTING: NavigationItem = {
  devices: [],
  key: 'static-sites-manage-routing',
  label: CONFIGURATION_STRINGS.manageRouting,
  path: screenPaths.staticSitesManageRouting,
  screen: <ManageRouting />,
};
